import { format } from "date-fns";
import { generatePath, Link } from "react-router-dom";
import { t } from "ttag";

import { ADMIN_ROUTES } from "@/app/router";
import {
  SESSION_COLOR_MAP,
  SESSION_ICON_MAP,
} from "@/features/coaching/models";
import type { AdminSession } from "@/features/coaching/models";
import { LogoLoader } from "@/shared/components";
import { formatTime } from "@/shared/lib";
import {
  CheckCircleIcon,
  ClockIcon,
  DetailCard,
  DropdownMenu,
  IconWrapper,
  MenuDotsIcons,
  PenIcon,
  TrashBinIcon,
  UserCircleIcon,
  UsersGroupTwoRoundedIcon,
} from "@/shared/ui";
import { useUpdateSession } from "../../hooks";

interface DraftSessionCardProps {
  session: AdminSession;
  onDelete: (session: AdminSession) => void;
}

export const DraftSessionCard = ({
  session,
  onDelete,
}: DraftSessionCardProps) => {
  const updateSessionMutation = useUpdateSession();

  const handlePublish = () =>
    updateSessionMutation.mutate({ ...session, published: true });

  return (
    <DetailCard.Root color={SESSION_COLOR_MAP[session.category]}>
      <DetailCard.Header>
        <IconWrapper size="sm">
          {SESSION_ICON_MAP[session.category]}
        </IconWrapper>
        <DetailCard.Title className="line-clamp-2">
          {session.title}
        </DetailCard.Title>
      </DetailCard.Header>

      <DetailCard.Content>
        <div className="mt-auto flex flex-wrap gap-2 sm:flex-nowrap">
          <DetailCard.Badge>
            {session.startAt ? format(session.startAt, "MM/dd/yy") : "-"}
          </DetailCard.Badge>
          <DetailCard.Badge outline>
            {formatTime(session.startAt)}
          </DetailCard.Badge>
        </div>

        <div className="flex flex-row gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <UserCircleIcon className="size-3.5" />
            <DetailCard.Label>{t`Coach`}</DetailCard.Label>
            <DetailCard.Description className="truncate">
              {session.coach?.name ?? "-"}
            </DetailCard.Description>
          </div>

          <div className="flex items-center gap-1">
            <DetailCard.Description>{session.duration}</DetailCard.Description>
            <DetailCard.Label>{t`min`}</DetailCard.Label>
            <ClockIcon className="size-3.5" />
          </div>
        </div>
      </DetailCard.Content>

      <DetailCard.Separator />

      <DetailCard.Footer>
        <div className="flex items-center gap-1">
          <UsersGroupTwoRoundedIcon className="size-3.5" />
          <DetailCard.Description>{t`Enrolled users will appear here`}</DetailCard.Description>
        </div>

        <div className="flex gap-1">
          <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger
              asChild
              className="data-[state=open]:bg-white"
            >
              <DetailCard.Button>
                <MenuDotsIcons className="size-3.5 rotate-90" />
              </DetailCard.Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Content align="end">
              <Link
                to={generatePath(ADMIN_ROUTES.COACHING.EDIT_SESSION, {
                  sessionId: String(session.id),
                })}
              >
                <DropdownMenu.Item className="gap-2">
                  <PenIcon className="size-3.5 text-primary-950" />
                  {t`Edit`}
                </DropdownMenu.Item>
              </Link>

              <DropdownMenu.Separator />

              <DropdownMenu.Item onClick={handlePublish} className="gap-2">
                <IconWrapper size="sm" className="text-neutral-800">
                  <CheckCircleIcon />
                </IconWrapper>
                {t`Publish`}
              </DropdownMenu.Item>

              <DropdownMenu.Separator />

              <DropdownMenu.Item
                onClick={() => onDelete?.(session)}
                className="gap-2"
              >
                <IconWrapper size="sm" className="text-neutral-800">
                  <TrashBinIcon className="text-red-800" />
                </IconWrapper>
                {t`Delete`}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </div>
      </DetailCard.Footer>

      <LogoLoader show={updateSessionMutation.isPending} fullscreen />
    </DetailCard.Root>
  );
};
